import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { fullSize } from '@toilet-paper-games/ui';

export const iFrameAllowList = [
  'accelerometer',
  'autoplay',
  'bluetooth',
  'camera',
  'fullscreen',
  'gamepad',
  'geolocation',
  'gyroscope',
  'hid',
  'keyboard-map',
  'magnetometer',
  'microphone',
  'midi',
  'payment',
  'screen-wake-lock',
  'usb',
  'xr-spatial-tracking',
];

export interface IFrameLoaderProps {
  title: string;
  src: string;
  className?: string;
  onMessage(message: string): void;
}

export interface IFrameLoaderRef {
  iframe: HTMLIFrameElement | null;
  reload: () => void;
}

export const IFrameLoader = forwardRef(function IFrameLoader(
  props: IFrameLoaderProps,
  ref: React.Ref<IFrameLoaderRef>,
) {
  const { onMessage, className, title, src } = props;

  const [reloadCount, setReloadCount] = useState(0);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const reload = useCallback(() => {
    if (iframeRef.current) {
      iframeRef.current.src += '';
    }
    setReloadCount((c) => c + 1);
  }, [iframeRef.current]);

  useImperativeHandle(
    ref,
    () => ({
      iframe: iframeRef.current,
      reload,
    }),
    [reload, iframeRef.current],
  );

  useEffect(() => {
    const iframe = iframeRef.current;

    if (!iframe) {
      return;
    }

    const messageListener = (event: MessageEvent<string>) => {
      // NOTE: Commented this out as it seems to be unnecessary but possibly could help with security
      // Ensure the message is from the expected origin (iframe's src)
      // const iframeOrigin = new URL(src).origin;
      // if (event.origin !== iframeOrigin) {
      //   return;
      // }

      // Ensure the message is from this specific iframe instance
      if (event.source !== iframe.contentWindow) {
        return;
      }

      // Handle the message
      const message = event.data;
      onMessage(message);
    };

    window.addEventListener('message', messageListener);

    return () => {
      window.removeEventListener('message', messageListener);
    };
  }, [reloadCount, iframeRef.current, onMessage, src]);

  return (
    <div className={className} css={fullSize}>
      <iframe
        ref={iframeRef}
        title={title}
        css={[{ border: 'none' }, fullSize]}
        src={src}
        sandbox="allow-scripts allow-same-origin allow-presentation allow-orientation-lock allow-pointer-lock allow-forms allow-downloads allow-popups allow-popups-to-escape-sandbox allow-storage-access-by-user-activation"
        allow={iFrameAllowList.join(';')}
      />
    </div>
  );
});
